// SalesSubscriptionsPage.jsx - Version refondue avec design de SalesPage
import React, { useEffect, useState, useMemo } from "react";
import { useSubscriptionStore } from "../../store/subscriptionStore";
import { toast } from "react-toastify";
import { FaWhatsapp, FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import {
  BiCalendar,
  BiReset,
  BiSearch,
  BiPackage,
  BiUser,
  BiListUl,
  BiX,
  BiDollar,
  BiTime,
  BiCheckCircle,
  BiCopy,
  BiXCircle,
  BiCheck,
  BiCreditCard,
  BiChevronLeft,
  BiChevronRight,
  BiDetail,
  BiPhone,
  BiReceipt,
} from "react-icons/bi";

// Composant Modal de Détails
function SubscriptionDetailModal({ isOpen, onClose, subscription }) {
  if (!isOpen || !subscription) return null;

  // Déterminer le client
  const client = subscription.client || subscription.anonymous_data;
  const isAnonymous = !subscription.client && subscription.anonymous_data;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="font-poppins font-semibold text-xl text-title">
              Détails de l'abonnement #{subscription.code}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 flex items-center gap-1">
                <BiPackage />
                Abonnement
              </span>
              <span
                className={`px-3 py-1 text-sm font-medium ${
                  subscription.status === "ACTIVE"
                    ? "bg-green-100 text-green-800"
                    : subscription.status === "PENDING"
                      ? "bg-yellow-100 text-yellow-800"
                      : subscription.status === "EXPIRED"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                }`}
              >
                {subscription.status === "ACTIVE"
                  ? "Actif"
                  : subscription.status === "PENDING"
                    ? "En attente"
                    : subscription.status === "EXPIRED"
                      ? "Expiré"
                      : "Annulé"}
              </span>
              {isAnonymous && (
                <span className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800">
                  Anonyme
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informations client */}
            <div className="bg-gray-50 p-4">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3 flex items-center gap-2">
                <BiUser />
                {isAnonymous ? "Informations du bénéficiaire" : "Client"}
              </h3>
              {client ? (
                <div className="space-y-3">
                  <div>
                    <p className="font-nunito text-sm text-text">Nom complet</p>
                    <p className="font-poppins font-medium">
                      {client.prenom
                        ? `${client.prenom} ${client.nom}`
                        : client.name || "Non spécifié"}
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">Contact</p>
                    <div className="flex gap-3 mt-2">
                      {client.email && (
                        <a
                          href={`mailto:${client.email}`}
                          className="text-gray-600 hover:text-gray-800"
                          title="Envoyer un email"
                        >
                          <FaEnvelope size={20} />
                        </a>
                      )}
                      {(client.telephone || client.tel) && (
                        <a
                          href={`tel:${client.telephone || client.tel}`}
                          className="text-secondary-600 hover:text-secondary-800"
                          title="Appeler"
                        >
                          <BiPhone size={20} />
                        </a>
                      )}
                      {(client.whatsapp || client.telephone || client.tel) && (
                        <a
                          href={`https://wa.me/${client.whatsapp || client.telephone || client.tel}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                          title="WhatsApp"
                        >
                          <FaWhatsapp size={20} />
                        </a>
                      )}
                    </div>
                  </div>
                  {client.email && (
                    <div>
                      <p className="font-nunito text-sm text-text">Email</p>
                      <p className="font-poppins font-medium">{client.email}</p>
                    </div>
                  )}
                  {(client.telephone || client.tel) && (
                    <div>
                      <p className="font-nunito text-sm text-text">Téléphone</p>
                      <p className="font-poppins font-medium">
                        {client.telephone || client.tel}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="font-nunito text-text">Client non spécifié</p>
              )}
            </div>

            {/* Informations de l'abonnement */}
            <div className="bg-gray-50 p-4">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3 flex items-center gap-2">
                <BiCreditCard />
                Abonnement
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="font-nunito text-sm text-text">Code</p>
                  <div className="flex items-center gap-2">
                    <p className="font-poppins font-medium">
                      {subscription.code}
                    </p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(subscription.code);
                        toast.success("Code copié dans le presse-papier");
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      title="Copier le code"
                    >
                      <BiCopy size={16} />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="font-nunito text-sm text-text">Montant</p>
                  <p className="font-poppins font-bold text-xl text-montant">
                    {parseFloat(
                      subscription.amount || subscription.totalAmount || 0,
                    ).toLocaleString()}{" "}
                    FCFA
                  </p>
                </div>
                <div>
                  <p className="font-nunito text-sm text-text">
                    Date de souscription
                  </p>
                  <p className="font-poppins font-medium">
                    {new Date(subscription.created_at).toLocaleString("fr-FR")}
                  </p>
                </div>
                {subscription.end_at && (
                  <div>
                    <p className="font-nunito text-sm text-text">Date de fin</p>
                    <p className="font-poppins font-medium">
                      {new Date(subscription.end_at).toLocaleDateString(
                        "fr-FR",
                      )}
                    </p>
                  </div>
                )}
                <div>
                  <p className="font-nunito text-sm text-text">Statut</p>
                  <span
                    className={`px-2 py-1 text-sm ${
                      subscription.status === "ACTIVE"
                        ? "bg-green-100 text-green-800"
                        : subscription.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : subscription.status === "EXPIRED"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {subscription.status === "ACTIVE"
                      ? "Actif"
                      : subscription.status === "PENDING"
                        ? "En attente"
                        : subscription.status === "EXPIRED"
                          ? "Expiré"
                          : "Annulé"}
                  </span>
                </div>
              </div>
            </div>

            {/* Informations du plan d'abonnement */}
            {subscription.plan && (
              <div className="md:col-span-2 bg-gray-50 p-4">
                <h3 className="font-poppins font-semibold text-lg text-title mb-3 flex items-center gap-2">
                  <BiPackage />
                  Plan d'abonnement
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-nunito text-sm text-text">Nom du plan</p>
                    <p className="font-poppins font-medium">
                      {subscription.plan.name}
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">
                      Code du plan
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="font-poppins font-medium">
                        {subscription.plan.code}
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(subscription.plan.code);
                          toast.success("Code du plan copié");
                        }}
                        className="text-gray-400 hover:text-gray-600"
                        title="Copier le code du plan"
                      >
                        <BiCopy size={16} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">
                      Prix du plan
                    </p>
                    <p className="font-poppins font-medium">
                      {parseFloat(
                        subscription.plan.price || 0,
                      ).toLocaleString()}{" "}
                      FCFA
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">
                      Statut du plan
                    </p>
                    <span
                      className={`px-2 py-1 text-sm ${
                        subscription.plan.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {subscription.plan.isActive ? "Actif" : "Inactif"}
                    </span>
                  </div>
                  {subscription.plan.description && (
                    <div className="md:col-span-2">
                      <p className="font-nunito text-sm text-text">
                        Description
                      </p>
                      <p className="font-poppins font-medium">
                        {subscription.plan.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Services inclus */}
            <div className="md:col-span-2">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3 flex items-center gap-2">
                <BiListUl />
                Services inclus ({subscription.services?.length || 0})
              </h3>
              <div className="space-y-3">
                {subscription.services?.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-white border"
                  >
                    <div className="flex-1">
                      <p className="font-poppins font-medium">
                        {item.service?.nom || `Service ${index + 1}`}
                      </p>
                      {item.service?.description && (
                        <p className="font-nunito text-sm text-text mt-1">
                          {item.service.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-1">
                          <BiCreditCard size={14} className="text-gray-500" />
                          <span className="font-nunito text-xs text-text">
                            Code: {item.code || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <BiCheckCircle
                            size={14}
                            className={
                              item.used ? "text-green-500" : "text-gray-500"
                            }
                          />
                          <span className="font-nunito text-xs text-text">
                            {item.used ? "Utilisé" : "Non utilisé"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {item.service?.price && (
                        <p className="font-poppins font-bold text-montant">
                          {parseFloat(item.service.price).toLocaleString()} FCFA
                        </p>
                      )}
                      {/* <p className="font-nunito text-sm text-text">
                        {item.service?.durationMinutes || "N/A"} minutes
                      </p> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 font-nunito hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant Card pour mobile
function SubscriptionCard({ subscription, onViewDetails }) {
  // Déterminer le client
  const client = subscription.client || subscription.anonymous_data;
  const clientName = client
    ? client.prenom
      ? `${client.prenom} ${client.nom}`
      : client.name || "Client anonyme"
    : "Client non spécifié";

  const displayName =
    clientName.length > 20 ? clientName.substring(0, 20) + "..." : clientName;

  const isAnonymous = !subscription.client && subscription.anonymous_data;

  return (
    <div className="bg-white border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow">
      {/* Ligne 1: Nom client + Statut */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span
              className="font-nunito font-medium text-sm truncate"
              title={clientName}
            >
              {displayName}
            </span>
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 flex items-center gap-1 shrink-0">
              <BiPackage size={12} />
              Abonnement
            </span>
          </div>
          <span
            className={`px-2 py-1 text-xs ${
              subscription.status === "ACTIVE"
                ? "bg-green-100 text-green-800"
                : subscription.status === "PENDING"
                  ? "bg-yellow-100 text-yellow-800"
                  : subscription.status === "EXPIRED"
                    ? "bg-red-100 text-red-800"
                    : "bg-gray-100 text-gray-800"
            } shrink-0`}
          >
            {subscription.status === "ACTIVE"
              ? "Actif"
              : subscription.status === "PENDING"
                ? "En attente"
                : subscription.status === "EXPIRED"
                  ? "Expiré"
                  : "Annulé"}
          </span>
        </div>

        {/* Ligne 2: Plan + Montant */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BiCreditCard className="text-gray-400" size={16} />
            <span className="font-nunito font-medium text-sm">
              {subscription.plan?.name || "N/A"}
            </span>
          </div>
          <span className="font-poppins font-bold text-base text-montant">
            {parseFloat(
              subscription.amount || subscription.totalAmount || 0,
            ).toLocaleString()}{" "}
            FCFA
          </span>
        </div>
      </div>

      {/* Ligne 3: Services + Date + Contacts */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <BiListUl size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {subscription.services?.length || 0} service
                {subscription.services?.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <BiCalendar size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {new Date(subscription.created_at).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {client && client.email && (
              <a
                href={`mailto:${client.email}`}
                className="text-gray-500 hover:text-gray-700"
                title="Envoyer un email"
              >
                <FaEnvelope size={14} />
              </a>
            )}
            {client && (client.telephone || client.tel) && (
              <a
                href={`tel:${client.telephone || client.tel}`}
                className="text-secondary-500 hover:text-secondary-700"
                title="Appeler"
              >
                <BiPhone size={14} />
              </a>
            )}
            {client && (client.whatsapp || client.telephone || client.tel) && (
              <a
                href={`https://wa.me/${client.whatsapp || client.telephone || client.tel}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-500 hover:text-green-700"
                title="WhatsApp"
              >
                <FaWhatsapp size={14} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Ligne 4: Code + Bouton Détails */}
      <div className="p-3 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-nunito text-xs text-text">Code:</span>
          <span className="font-mono text-xs font-medium bg-gray-100 px-2 py-1">
            {subscription.code}
          </span>
        </div>

        <button
          onClick={() => onViewDetails(subscription)}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <BiDetail size={16} />
          Détails
        </button>
      </div>
    </div>
  );
}

export default function SalesSubscriptionsPage() {
  const {
    subscriptions,
    loading,
    error,
    fetchSubscriptions,
    fetchSubscriptionById,
  } = useSubscriptionStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // États pour les filtres de période
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");

  // États pour le tri
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Chargement initial
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      await fetchSubscriptions();
    } catch (error) {
      console.error("Erreur lors du chargement initial:", error);
    }
  };

  // Fonction pour obtenir les dates d'une période
  const getPeriodDates = (period) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    switch (period) {
      case "today":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;

      case "week":
        start.setDate(today.getDate() - today.getDay());
        start.setHours(0, 0, 0, 0);
        end.setDate(today.getDate() + (6 - today.getDay()));
        end.setHours(23, 59, 59, 999);
        break;

      case "month":
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;

      default:
        return { startDate: "", endDate: "" };
    }

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
    };
  };

  const handlePeriodChange = async (period) => {
    setPeriodFilter(period);

    if (period === "all") {
      setStartDate("");
      setEndDate("");
    } else if (period !== "custom") {
      const dates = getPeriodDates(period);
      setStartDate(dates.startDate);
      setEndDate(dates.endDate);
    }
  };

  const applyCustomFilter = () => {
    if (periodFilter === "custom" && startDate && endDate) {
      toast.success(`Filtre appliqué du ${startDate} au ${endDate}`);
    } else {
      toast.warning("Veuillez sélectionner une date de début et de fin");
    }
  };

  // Fonction pour ouvrir le modal des détails
  const handleShowDetails = async (subscription) => {
    setSelectedSubscription(subscription);
    setIsModalOpen(true);
  };

  // Fonction pour fermer le modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSubscription(null);
  };

  const resetFilters = async () => {
    setPeriodFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setStatusFilter("");
    setSortField("created_at");
    setSortDirection("desc");
    setCurrentPage(1);
  };

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case "today":
        return "Aujourd'hui";
      case "week":
        return "Cette semaine";
      case "month":
        return "Ce mois";
      case "custom":
        return startDate && endDate
          ? `Période du ${new Date(startDate).toLocaleDateString(
              "fr-FR",
            )} au ${new Date(endDate).toLocaleDateString("fr-FR")}`
          : "Période personnalisée";
      default:
        return "Toutes les périodes";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "ACTIVE":
        return "Actif";
      case "EXPIRED":
        return "Expiré";
      case "CANCELLED":
        return "Annulé";
      case "PENDING":
        return "En attente";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "EXPIRED":
        return "bg-red-100 text-red-800";
      case "CANCELLED":
        return "bg-gray-100 text-gray-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Fonction pour obtenir le nom du client
  const getClientName = (subscription) => {
    const client = subscription.client || subscription.anonymous_data;
    if (client) {
      if (client.prenom && client.nom) {
        return `${client.prenom} ${client.nom}`.trim();
      } else if (client.name) {
        return client.name;
      }
    }
    return "Client non spécifié";
  };

  // Fonction pour obtenir le montant
  const getAmount = (subscription) => {
    return parseFloat(subscription.amount || subscription.totalAmount || 0);
  };

  // Filtrage et tri des abonnements
  const filteredAndSortedSubscriptions = useMemo(() => {
    if (!subscriptions) return [];

    let filtered = subscriptions.filter((subscription) => {
      // Filtre par recherche
      const matchesSearch =
        !searchTerm ||
        subscription.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subscription.plan?.name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        getClientName(subscription)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      // Filtre par statut
      const matchesStatus =
        !statusFilter || subscription.status === statusFilter;

      // Filtre par période
      let matchesDate = true;
      if (periodFilter !== "all" && startDate && endDate) {
        const subDate = new Date(subscription.created_at);
        const filterStartDate = new Date(startDate);
        const filterEndDate = new Date(endDate);

        // Définir les heures pour couvrir toute la journée
        filterStartDate.setHours(0, 0, 0, 0);
        filterEndDate.setHours(23, 59, 59, 999);

        matchesDate = subDate >= filterStartDate && subDate <= filterEndDate;
      }

      return matchesSearch && matchesStatus && matchesDate;
    });

    // Tri des résultats
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "client":
          aValue = getClientName(a);
          bValue = getClientName(b);
          break;
        case "code":
          aValue = a.code || "";
          bValue = b.code || "";
          break;
        case "plan":
          aValue = a.plan?.name || "";
          bValue = b.plan?.name || "";
          break;
        case "services":
          aValue = a.services?.length || 0;
          bValue = b.services?.length || 0;
          break;
        case "amount":
          aValue = getAmount(a);
          bValue = getAmount(b);
          break;
        case "status":
          aValue = a.status || "";
          bValue = b.status || "";
          break;
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          aValue = a[sortField];
          bValue = b[sortField];
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [
    subscriptions,
    searchTerm,
    statusFilter,
    periodFilter,
    startDate,
    endDate,
    sortField,
    sortDirection,
  ]);

  // Pagination
  const totalPages = Math.ceil(
    filteredAndSortedSubscriptions.length / itemsPerPage,
  );
  const paginatedSubscriptions = filteredAndSortedSubscriptions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Colonnes pour le tableau desktop
  const columns = [
    {
      headerName: "Client",
      field: "client",
      width: "20%",
      sortable: true,
      render: (subscription) => {
        const clientName = getClientName(subscription);
        const displayName =
          clientName.length > 25
            ? clientName.substring(0, 25) + "..."
            : clientName;

        const client = subscription.client || subscription.anonymous_data;
        const isAnonymous = !subscription.client && subscription.anonymous_data;

        return (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="font-nunito font-normal text-sm truncate"
                title={clientName}
              >
                {displayName}
              </span>
              {isAnonymous && (
                <span className="px-1 py-0.5 text-xs bg-purple-100 text-purple-800">
                  Anonyme
                </span>
              )}
              <div className="flex gap-1 shrink-0">
                {client?.email && (
                  <a
                    href={`mailto:${client.email}`}
                    className="text-gray-500 hover:text-gray-700"
                    title="Envoyer un email"
                  >
                    <FaEnvelope size={12} />
                  </a>
                )}
                {(client?.telephone || client?.tel) && (
                  <a
                    href={`tel:${client.telephone || client.tel}`}
                    className="text-secondary-500 hover:text-secondary-700"
                    title="Appeler"
                  >
                    <BiPhone size={12} />
                  </a>
                )}
                {(client?.whatsapp || client?.telephone || client?.tel) && (
                  <a
                    href={`https://wa.me/${client.whatsapp || client.telephone || client.tel}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-500 hover:text-green-700"
                    title="WhatsApp"
                  >
                    <FaWhatsapp size={12} />
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Code",
      field: "code",
      width: "12%",
      sortable: true,
      render: (subscription) => (
        <div className="font-mono text-xs font-medium">{subscription.code}</div>
      ),
    },
    {
      headerName: "Plan",
      field: "plan",
      width: "18%",
      sortable: true,
      render: (subscription) => (
        <div className="font-nunito text-sm">
          {subscription.plan?.name || "N/A"}
        </div>
      ),
    },
    {
      headerName: "Services",
      field: "services",
      width: "8%",
      sortable: true,
      render: (subscription) => (
        <div className="flex items-center gap-1">
          <BiListUl size={14} className="text-gray-500" />
          <span className="font-nunito text-xs">
            {subscription.services?.length || 0}
          </span>
        </div>
      ),
    },
    {
      headerName: "Montant",
      field: "amount",
      width: "12%",
      sortable: true,
      render: (subscription) => (
        <div className="font-poppins font-bold text-sm text-montant">
          {getAmount(subscription).toLocaleString()} FCFA
        </div>
      ),
    },
    {
      headerName: "Statut",
      field: "status",
      width: "10%",
      sortable: true,
      render: (subscription) => (
        <span
          className={`px-2 py-1 text-xs ${getStatusColor(subscription.status)}`}
        >
          {getStatusLabel(subscription.status)}
        </span>
      ),
    },
    {
      headerName: "Date",
      field: "created_at",
      width: "10%",
      sortable: true,
      render: (subscription) => (
        <div className="font-nunito text-xs text-text">
          {new Date(subscription.created_at).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </div>
      ),
    },
    {
      headerName: "",
      width: "10%",
      render: (subscription) => (
        <button
          onClick={() => handleShowDetails(subscription)}
          className="w-full px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
        >
          <BiDetail size={14} />
          Détails
        </button>
      ),
    },
  ];

  // Calcul des statistiques sur les données filtrées
  const totalSubscriptions = filteredAndSortedSubscriptions.length;
  const activeSubscriptions = filteredAndSortedSubscriptions.filter(
    (s) => s.status === "ACTIVE",
  ).length;
  const totalAmount = filteredAndSortedSubscriptions.reduce(
    (sum, s) => sum + getAmount(s),
    0,
  );

  return (
    <div className="md:p-6">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="font-poppins font-semibold text-[18px] text-title">
          Les Abonnements
        </h1>
        <p className="font-nunito text-[14px] text-text mt-1">
          Historique de tous les abonnements souscrits
        </p>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white p-4 shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col 2xl:flex-row gap-4">
          {/* Barre de recherche */}
          <div className="flex-1 relative">
            <BiSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Rechercher par code, nom du plan ou client..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
            />
          </div>

          {/* Filtres de période et statut */}
          <div className="flex flex-col xl:flex-row justify-between gap-3">
            {/* Sélecteur de période rapide */}
            {/* <select
              value={periodFilter}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
            >
              <option value="all">Toutes les périodes</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="custom">Période personnalisée</option>
            </select> */}

            {/* Dates personnalisées */}
            {/* {periodFilter === "custom" && (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex flex-1 justify-end items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                  <BiCalendar className="text-gray-400" size={16} />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="focus:outline-none font-nunito text-[14px] w-32"
                  />
                </div>
                <div className="flex flex-1 justify-end items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                  <BiCalendar className="text-gray-400" size={16} />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="focus:outline-none font-nunito text-[14px] w-32"
                  />
                </div>
                <button
                  onClick={applyCustomFilter}
                  className="flex items-center justify-center gap-2 px-3 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors"
                >
                  <BiCheck size={14} />
                  Appliquer
                </button>
              </div>
            )} */}

            {/* Filtre par statut */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
            >
              <option value="">Tous les statuts</option>
              <option value="ACTIVE">Actif</option>
              <option value="PENDING">En attente</option>
              <option value="EXPIRED">Expiré</option>
              <option value="CANCELLED">Annulé</option>
            </select>

            {/* Bouton réinitialiser */}
            {(periodFilter !== "all" ||
              searchTerm ||
              statusFilter ||
              sortField !== "created_at" ||
              sortDirection !== "desc") && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-3 py-2 font-nunito text-[14px] font-semibold text-action text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                <BiReset size={16} />
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Indicateur de période active */}
        {(periodFilter !== "all" || searchTerm || statusFilter) && (
          <div className="mt-3 flex items-center gap-2 font-nunito text-[14px]">
            <span className="text-gray-600">Filtres actifs:</span>
            {periodFilter !== "all" && (
              <span className="bg-secondary-100 text-secondary-800 px-2 py-1">
                {getPeriodLabel()}
              </span>
            )}
            {searchTerm && (
              <span className="bg-green-100 text-green-800 px-2 py-1">
                Recherche: "{searchTerm}"
              </span>
            )}
            {statusFilter && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1">
                Statut: {getStatusLabel(statusFilter)}
              </span>
            )}
            <span className="bg-gray-100 text-gray-800 px-2 py-1">
              {filteredAndSortedSubscriptions.length} résultat
              {filteredAndSortedSubscriptions.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2">
            <BiPackage className="text-green-600" size={20} />
            <p className="font-poppins font-medium text-[15px] text-subtitle">
              Total abonnements
            </p>
          </div>
          <p className="font-poppins font-bold text-[16px] text-green-600 mt-2">
            {totalSubscriptions}
          </p>
        </div>
        <div className="bg-white p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2">
            <BiCheckCircle className="text-blue-600" size={20} />
            <p className="font-poppins font-medium text-[15px] text-subtitle">
              Abonnements actifs
            </p>
          </div>
          <p className="font-poppins font-bold text-[16px] text-blue-600 mt-2">
            {activeSubscriptions}
          </p>
        </div>
        <div className="bg-white p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-2">
            <BiDollar className="text-secondary-600" size={20} />
            <p className="font-poppins font-medium text-[15px] text-subtitle">
              Montant total
            </p>
          </div>
          <p className="font-poppins font-bold text-[16px] text-secondary-600 mt-2">
            {totalAmount.toLocaleString()} FCFA
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600"></div>
        </div>
      ) : (
        <>
          {/* Pagination en haut */}
          {totalPages > 1 && filteredAndSortedSubscriptions.length > 0 && (
            <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} •{" "}
                {filteredAndSortedSubscriptions.length} élément
                {filteredAndSortedSubscriptions.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Tableau Desktop */}
          <div className="hidden lg:block bg-white border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        className="px-4 py-3 text-left font-poppins font-bold text-xs text-title border-b border-gray-200"
                        style={{ width: column.width }}
                      >
                        {column.sortable ? (
                          <button
                            onClick={() => handleSort(column.field)}
                            className="flex items-center gap-1 hover:text-secondary-600"
                          >
                            {column.headerName}
                            {sortField === column.field && (
                              <span className="text-xs">
                                {sortDirection === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </button>
                        ) : (
                          column.headerName
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y font-poppins divide-gray-200">
                  {paginatedSubscriptions.map((subscription, index) => (
                    <tr
                      key={subscription.id}
                      className={
                        index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50 hover:bg-gray-100"
                      }
                    >
                      {columns.map((column, colIndex) => (
                        <td key={colIndex} className="px-4 py-3">
                          <div className="flex items-center h-full">
                            {column.render(subscription)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cartes Mobile */}
          <div className="lg:hidden space-y-3 mb-6">
            {paginatedSubscriptions.map((subscription) => (
              <SubscriptionCard
                key={subscription.id}
                subscription={subscription}
                onViewDetails={handleShowDetails}
              />
            ))}
          </div>

          {/* Pagination en bas */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} •{" "}
                {filteredAndSortedSubscriptions.length} élément
                {filteredAndSortedSubscriptions.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Message vide */}
          {filteredAndSortedSubscriptions.length === 0 && (
            <div className="text-center py-12 bg-white border border-gray-200">
              <BiPackage className="mx-auto text-gray-300" size={40} />
              <p className="font-nunito text-sm text-text mt-3">
                {searchTerm || statusFilter || periodFilter !== "all"
                  ? "Aucun résultat trouvé"
                  : "Aucun abonnement pour cette période"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Modal de détails */}
      <SubscriptionDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        subscription={selectedSubscription}
      />
    </div>
  );
}

// // Pages/SubscriptionsPage.jsx
// import React, { useEffect, useState } from "react";
// import { useSubscriptionStore } from "../../store/subscriptionStore";
// import DataTable from "../../common/DataTable";
// import { toast } from "react-toastify";
// import { FaWhatsapp, FaEnvelope, FaPhoneAlt } from "react-icons/fa";

// import {
//   BiCalendar,
//   BiReset,
//   BiSearch,
//   BiPackage,
//   BiUser,
//   BiListUl,
//   BiX,
//   BiDollar,
//   BiTime,
//   BiCheckCircle,
//   BiCopy,
//   BiXCircle,
//   BiCheck,
//   BiCreditCard,
// } from "react-icons/bi";
// import useGiftCardStore from "../../store/useGiftCardStore";

// export default function SalesSubscriptionsPage() {
//   const {
//     subscriptions,
//     loading,
//     error,
//     fetchSubscriptions,
//     fetchSubscriptionById,
//   } = useSubscriptionStore();

//   const { getAllGiftCards, giftCards, isLoading, setAsUsedGiftCard } =
//     useGiftCardStore();

//   const [searchTerm, setSearchTerm] = useState("");
//   const [statusFilter, setStatusFilter] = useState("");
//   const [showDetailsModal, setShowDetailsModal] = useState(false);
//   const [selectedSubscription, setSelectedSubscription] = useState(null);
//   const [detailedSubscription, setDetailedSubscription] = useState(null);
//   const [modalLoading, setModalLoading] = useState(false);

//   // États pour les filtres de période
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");
//   const [periodFilter, setPeriodFilter] = useState("all");

//   // Chargement initial
//   useEffect(() => {
//     loadInitialData();
//   }, []);

//   const loadInitialData = async () => {
//     try {
//       await fetchSubscriptions();
//     } catch (error) {
//       console.error("Erreur lors du chargement initial:", error);
//     }
//   };

//   // Fonction pour obtenir les dates d'une période
//   const getPeriodDates = (period) => {
//     const today = new Date();
//     let start = new Date(today);
//     let end = new Date(today);

//     const formatDate = (date) => {
//       const y = date.getFullYear();
//       const m = String(date.getMonth() + 1).padStart(2, "0");
//       const d = String(date.getDate()).padStart(2, "0");
//       return `${y}-${m}-${d}`;
//     };

//     switch (period) {
//       case "today":
//         start.setHours(0, 0, 0, 0);
//         end.setHours(23, 59, 59, 999);
//         break;

//       case "week":
//         start.setDate(today.getDate() - today.getDay());
//         start.setHours(0, 0, 0, 0);
//         end.setDate(today.getDate() + (6 - today.getDay()));
//         end.setHours(23, 59, 59, 999);
//         break;

//       case "month":
//         start.setDate(1);
//         start.setHours(0, 0, 0, 0);
//         end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
//         end.setHours(23, 59, 59, 999);
//         break;

//       default:
//         return { startDate: "", endDate: "" };
//     }

//     return {
//       startDate: formatDate(start),
//       endDate: formatDate(end),
//     };
//   };

//   const handlePeriodChange = async (period) => {
//     setPeriodFilter(period);

//     if (period === "all") {
//       setStartDate("");
//       setEndDate("");
//     } else if (period !== "custom") {
//       const dates = getPeriodDates(period);
//       setStartDate(dates.startDate);
//       setEndDate(dates.endDate);
//     }
//     // Pour "custom", on attend que l'utilisateur sélectionne les dates
//   };

//   // Fonction pour appliquer manuellement le filtre personnalisé
//   const applyCustomFilter = () => {
//     if (periodFilter === "custom" && startDate && endDate) {
//       // Le filtrage se fait automatiquement via le computed filteredSubscriptions
//       toast.success(`Filtre appliqué du ${startDate} au ${endDate}`);
//     } else {
//       toast.warning("Veuillez sélectionner une date de début et de fin");
//     }
//   };

//   // Utiliser directement la fonction du store sans créer de hook
//   const handleUseCard = async (card) => {
//     if (
//       window.confirm(
//         `Êtes-vous sûr de vouloir marquer la carte ${card.code} comme utilisée ?`,
//       )
//     ) {
//       try {
//         await setAsUsedGiftCard(card.id);
//         toast.success("Carte marquée comme utilisée");
//         setModalLoading(true);

//         const detailedData = await fetchSubscriptionById(
//           selectedSubscription.id,
//         );
//         setDetailedSubscription(detailedData);
//         setModalLoading(false);
//       } catch (error) {
//         console.error("Erreur lors de l'utilisation de la carte:", error);
//         toast.error("Erreur lors de l'utilisation de la carte");
//       }
//     }
//   };

//   // Fonction pour ouvrir le modal des détails
//   const handleShowDetails = async (subscription) => {
//     setSelectedSubscription(subscription);
//     setShowDetailsModal(true);
//     setModalLoading(true);

//     try {
//       const detailedData = await fetchSubscriptionById(subscription.id);
//       setDetailedSubscription(detailedData);
//     } catch (error) {
//       console.error("Erreur lors du chargement des détails:", error);
//       toast.error("Erreur lors du chargement des détails de l'abonnement");
//       // En cas d'erreur, utiliser les données de base
//       setDetailedSubscription(subscription);
//     } finally {
//       setModalLoading(false);
//     }
//   };

//   // Fonction pour fermer le modal
//   const handleCloseModal = () => {
//     setShowDetailsModal(false);
//     setSelectedSubscription(null);
//     setDetailedSubscription(null);
//   };

//   const resetFilters = async () => {
//     setPeriodFilter("all");
//     setStartDate("");
//     setEndDate("");
//     setSearchTerm("");
//     setStatusFilter("");
//   };

//   const getPeriodLabel = () => {
//     switch (periodFilter) {
//       case "today":
//         return "Aujourd'hui";
//       case "week":
//         return "Cette semaine";
//       case "month":
//         return "Ce mois";
//       case "custom":
//         return startDate && endDate
//           ? `Période du ${new Date(startDate).toLocaleDateString(
//               "fr-FR",
//             )} au ${new Date(endDate).toLocaleDateString("fr-FR")}`
//           : "Période personnalisée";
//       default:
//         return "Toutes les périodes";
//     }
//   };

//   const getStatusLabel = (status) => {
//     switch (status) {
//       case "ACTIVE":
//         return "Actif";
//       case "EXPIRED":
//         return "Expiré";
//       case "CANCELLED":
//         return "Annulé";
//       case "PENDING":
//         return "En attente";
//       default:
//         return status;
//     }
//   };

//   const getStatusColor = (status) => {
//     switch (status) {
//       case "ACTIVE":
//         return "bg-green-100 text-green-800";
//       case "EXPIRED":
//         return "bg-red-100 text-red-800";
//       case "CANCELLED":
//         return "bg-gray-100 text-gray-800";
//       case "PENDING":
//         return "bg-yellow-100 text-yellow-800";
//       default:
//         return "bg-gray-100 text-gray-800";
//     }
//   };

//   const copyToClipboard = (text) => {
//     if (!text) return;
//     navigator.clipboard.writeText(text);
//     toast.success("Copié dans le presse-papier");
//   };

//   // Colonnes du tableau (inchangées)
//   const columns = [
//     // {
//     //   field: "id",
//     //   headerName: "ID",
//     //   width: 80,
//     //   hideable: true,
//     // },
//     {
//       field: "client",
//       headerName: "Client",
//       width: 150,
//       renderCell: ({ value, row }) => {
//         return (
//           <div className="flex items-center gap-2">
//             <span className="font-nunito text-[14px] text-text">
//               {`${row.client?.prenom || ""} ${
//                 row.client?.nom || "Non renseigné"
//               }`}
//             </span>

//             {/* Appel direct */}
//             {row.client?.email && (
//               <a
//                 href={`mailto:${row.client?.email}`}
//                 title="Envoyer le mail"
//                 className="text-gray-600 hover:text-gray-800"
//               >
//                 <FaEnvelope size={16} />
//               </a>
//             )}

//             {/* Appel direct */}
//             {row.client?.telephone && (
//               <a
//                 href={`tel:${row.client?.telephone}`}
//                 title="Composer le numero"
//                 className="text-secondary-600 hover:text-secondary-800"
//               >
//                 <FaPhoneAlt size={16} />
//               </a>
//             )}

//             {/* WhatsApp */}
//             {row.client?.whatsapp && (
//               <a
//                 href={`https://wa.me/${row.client?.whatsapp}`}
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 title="WhatsApp"
//                 className="text-green-600 hover:text-green-800"
//               >
//                 <FaWhatsapp size={16} />
//               </a>
//             )}
//           </div>
//         );
//       },
//     },
//     {
//       field: "code",
//       headerName: "Code",
//       width: 120,
//       renderCell: ({ row }) => (
//         <div
//           className="flex items-center gap-2 cursor-pointer font-nunito text-[14px] font-semibold text-action"
//           onClick={() => copyToClipboard(row.code)}
//         >
//           <span className="font-mono px-3 py-2 flex-1">{row.code}</span>
//           <button
//             onClick={(e) => {
//               e.stopPropagation();
//               copyToClipboard(row.code);
//             }}
//             className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
//           >
//             <BiCopy size={16} />
//           </button>
//         </div>
//       ),
//     },
//     {
//       field: "plan",
//       headerName: "Plan d'abonnement",
//       width: 180,
//       renderCell: ({ row }) => (
//         <div className="flex items-center gap-2">
//           <BiPackage className="text-secondary-600 flex-shrink-0" size={14} />
//           <span className="font-nunito text-[14px] text-text truncate">
//             {row.plan?.name || "N/A"}
//           </span>
//         </div>
//       ),
//     },
//     {
//       field: "includedServices",
//       headerName: "Services",
//       width: 100,
//       renderCell: ({ row }) => (
//         <span className="font-poppins font-bold text-[16px] text-montant">
//           {row.plan?.includedServices?.length || "N/A"}
//         </span>
//       ),
//     },
//     {
//       field: "quantity",
//       headerName: "Quantité",
//       width: 100,
//       renderCell: ({ row }) => (
//         <span className="font-poppins font-bold text-[16px] text-montant">
//           {row.quantity}
//         </span>
//       ),
//     },
//     {
//       field: "totalAmount",
//       headerName: "Montant",
//       width: 120,
//       renderCell: ({ row }) => (
//         <span className="font-poppins font-bold text-[16px] text-montant">
//           {parseFloat(row.totalAmount).toLocaleString()} FCFA
//         </span>
//       ),
//     },
//     {
//       field: "status",
//       headerName: "Statut",
//       width: 110,
//       renderCell: ({ row }) => (
//         <span
//           className={`px-2 py-1 font-nunito text-[14px] font-semibold text-action ${getStatusColor(
//             row.status,
//           )}`}
//         >
//           {getStatusLabel(row.status)}
//         </span>
//       ),
//     },
//     {
//       field: "created_at",
//       headerName: "Date souscription",
//       width: 130,
//       valueGetter: (value, row) =>
//         new Date(row.created_at).toLocaleDateString("fr-FR", {
//           day: "2-digit",
//           month: "2-digit",
//           year: "numeric",
//         }),
//     },
//     {
//       field: "actions",
//       headerName: "Actions",
//       width: 100,
//       renderCell: ({ row }) => (
//         <div>
//           <button
//             onClick={() => handleShowDetails(row)}
//             className="flex items-center gap-1 px-2 py-1 font-nunito text-[14px] font-semibold text-action bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors rounded"
//             title="Détails"
//           >
//             <BiListUl size={20} />
//           </button>
//         </div>
//       ),
//     },
//   ];

//   // Filtrage côté client pour tous les critères
//   const filteredSubscriptions = subscriptions.filter((subscription) => {
//     // Filtre par recherche
//     const matchesSearch =
//       !searchTerm ||
//       subscription.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       subscription.plan?.name
//         ?.toLowerCase()
//         .includes(searchTerm.toLowerCase()) ||
//       subscription.plan?.code
//         ?.toLowerCase()
//         .includes(searchTerm.toLowerCase()) ||
//       `${subscription.client?.prenom || ""} ${subscription.client?.nom || ""}`
//         .toLowerCase()
//         .includes(searchTerm.toLowerCase());

//     // Filtre par statut
//     const matchesStatus = !statusFilter || subscription.status === statusFilter;

//     // Filtre par période
//     let matchesDate = true;
//     if (periodFilter !== "all" && startDate && endDate) {
//       const subDate = new Date(subscription.created_at);
//       const filterStartDate = new Date(startDate);
//       const filterEndDate = new Date(endDate);

//       // Définir les heures pour couvrir toute la journée
//       filterStartDate.setHours(0, 0, 0, 0);
//       filterEndDate.setHours(23, 59, 59, 999);

//       matchesDate = subDate >= filterStartDate && subDate <= filterEndDate;
//     }

//     return matchesSearch && matchesStatus && matchesDate;
//   });

//   // Fonction pour obtenir le statut de la carte
//   const getCardStatusLabel = (status) => {
//     switch (status) {
//       case "UNUSED":
//         return "Non utilisée";
//       case "USED":
//         return "Utilisée";
//       case "EXPIRED":
//         return "Expirée";
//       default:
//         return status;
//     }
//   };

//   const getCardStatusColor = (status) => {
//     switch (status) {
//       case "UNUSED":
//         return "bg-green-100 text-green-800";
//       case "USED":
//         return "bg-blue-100 text-blue-800";
//       case "EXPIRED":
//         return "bg-red-100 text-red-800";
//       default:
//         return "bg-gray-100 text-gray-800";
//     }
//   };

//   const getCardStatusIcon = (status) => {
//     switch (status) {
//       case "UNUSED":
//         return <BiCheck className="text-green-600" size={14} />;
//       case "USED":
//         return <BiCheckCircle className="text-blue-600" size={14} />;
//       case "EXPIRED":
//         return <BiXCircle className="text-red-600" size={14} />;
//       default:
//         return <BiCreditCard className="text-gray-600" size={14} />;
//     }
//   };

//   // Calcul des statistiques sur les données filtrées
//   const totalSubscriptions = filteredSubscriptions.length;
//   const activeSubscriptions = filteredSubscriptions.filter(
//     (s) => s.status === "ACTIVE",
//   ).length;
//   const totalAmount = filteredSubscriptions.reduce(
//     (sum, s) => sum + parseFloat(s.totalAmount || 0),
//     0,
//   );

//   return (
//     <div className="mx-auto">
//       {/* En-tête */}
//       <div className="mb-4 sm:mb-6">
//         <h1 className="font-poppins font-semibold text-[18px] text-title">
//           Les Abonnements
//         </h1>
//         <p className="font-nunito text-[14px] text-text mt-1">
//           Historique de tous les abonnements souscrits
//         </p>
//       </div>

//       {/* Barre de recherche et filtres */}
//       <div className="bg-white p-3 sm:p-4 shadow-sm border border-gray-200 mb-4 sm:mb-6">
//         {/* Barre de recherche */}
//         <div className="relative mb-4">
//           <BiSearch
//             className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
//             size={18}
//           />
//           <input
//             type="text"
//             placeholder="Rechercher par code, nom du plan ou client..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//           />
//         </div>

//         {/* Filtres de période et statut - Layout responsive avec wrap */}
//         <div className="flex flex-col 2xl:flex-row gap-3">
//           {/* Colonne gauche - Filtres principaux */}
//           <div className="flex flex-col sm:flex-row gap-2">
//             {/* Sélecteur de période rapide */}
//             {/* Sélecteur de période rapide */}
//             <select
//               value={periodFilter}
//               onChange={(e) => handlePeriodChange(e.target.value)}
//               className="border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//             >
//               <option value="all">Toutes les périodes</option>
//               <option value="today">Aujourd'hui</option>
//               <option value="week">Cette semaine</option>
//               <option value="month">Ce mois</option>
//               <option value="custom">Période personnalisée</option>
//             </select>

//             <select
//               value={statusFilter}
//               onChange={(e) => setStatusFilter(e.target.value)}
//               className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
//             >
//               <option value="">Tous les statuts</option>
//               <option value="ACTIVE">Actif</option>
//               <option value="PENDING">En attente</option>
//               <option value="EXPIRED">Expiré</option>
//               <option value="CANCELLED">Annulé</option>
//             </select>
//           </div>

//           {/* Dates personnalisées */}
//           {periodFilter === "custom" && (
//             <div className="flex flex-col sm:flex-row gap-2 flex-1">
//               <div className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-2 flex-1 min-w-[150px]">
//                 <BiCalendar className="text-gray-400 flex-shrink-0" size={14} />
//                 <input
//                   type="date"
//                   value={startDate}
//                   onChange={(e) => setStartDate(e.target.value)}
//                   className="focus:outline-none font-nunito text-[14px] w-full"
//                 />
//               </div>
//               <div className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-2 flex-1 min-w-[150px]">
//                 <BiCalendar className="text-gray-400 flex-shrink-0" size={14} />
//                 <input
//                   type="date"
//                   value={endDate}
//                   onChange={(e) => setEndDate(e.target.value)}
//                   className="focus:outline-none font-nunito text-[14px] w-full"
//                 />
//               </div>
//               <button
//                 onClick={applyCustomFilter}
//                 className="flex items-center justify-center gap-2 px-3 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors min-w-[120px]"
//               >
//                 <BiCheck size={14} />
//                 Appliquer
//               </button>
//             </div>
//           )}

//           {/* Bouton réinitialiser */}
//           {(periodFilter !== "all" || searchTerm || statusFilter) && (
//             <button
//               onClick={resetFilters}
//               className="flex items-center justify-center gap-2 px-3 py-2 font-nunito text-[14px] font-semibold text-action text-gray-600 border border-gray-300 hover:bg-gray-50 min-w-[140px]"
//             >
//               <BiReset size={14} />
//               Réinitialiser
//             </button>
//           )}
//         </div>

//         {/* Indicateur de période active */}
//         {(periodFilter !== "all" || searchTerm || statusFilter) && (
//           <div className="mt-4 flex flex-wrap items-center gap-2 font-nunito text-[14px]">
//             <span className="text-gray-600">Filtres actifs:</span>
//             {periodFilter !== "all" && (
//               <span className="bg-secondary-100 text-secondary-800 px-2 py-1 rounded">
//                 {getPeriodLabel()}
//               </span>
//             )}
//             {searchTerm && (
//               <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
//                 Recherche: "{searchTerm}"
//               </span>
//             )}
//             {statusFilter && (
//               <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
//                 Statut: {getStatusLabel(statusFilter)}
//               </span>
//             )}
//             <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
//               {filteredSubscriptions.length} résultat
//               {filteredSubscriptions.length !== 1 ? "s" : ""}
//             </span>
//           </div>
//         )}
//       </div>

//       {/* Statistiques rapides */}
//       <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
//         <div className="bg-white p-3 sm:p-4 shadow-sm border border-gray-200">
//           <div className="flex items-center gap-2">
//             <BiPackage className="text-green-600 flex-shrink-0" size={16} />
//             <p className="font-poppins font-medium text-[15px] text-subtitle">
//               Total abonnements
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-green-600 mt-1 sm:mt-2">
//             {totalSubscriptions}
//           </p>
//         </div>
//         <div className="bg-white p-3 sm:p-4 shadow-sm border border-gray-200">
//           <div className="flex items-center gap-2">
//             <BiCheckCircle className="text-blue-600 flex-shrink-0" size={16} />
//             <p className="font-poppins font-medium text-[15px] text-subtitle">
//               Abonnements actifs
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-blue-600 mt-1 sm:mt-2">
//             {activeSubscriptions}
//           </p>
//         </div>
//         <div className="bg-white p-3 sm:p-4 shadow-sm border border-gray-200">
//           <div className="flex items-center gap-2">
//             <BiDollar className="text-secondary-600 flex-shrink-0" size={16} />
//             <p className="font-poppins font-medium text-[15px] text-subtitle">
//               Montant total
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-secondary-600 mt-1 sm:mt-2">
//             {totalAmount.toLocaleString()} FCFA
//           </p>
//         </div>
//       </div>

//       {/* Tableau des abonnements */}
//       <div className="bg-white shadow-sm border border-gray-200 overflow-hidden">
//         <DataTable
//           title={`Mes abonnements ${
//             periodFilter !== "all" ? `- ${getPeriodLabel()}` : ""
//           }`}
//           rows={filteredSubscriptions}
//           columns={columns}
//           loading={loading}
//           showToolbar={false}
//           autoHeight={true}
//           className="font-nunito text-[14px]"
//         />
//       </div>

//       {/* Modal des détails (inchangé) */}
//       {showDetailsModal && selectedSubscription && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
//           <div
//             className="absolute h-full w-full z-0 cursor-pointer"
//             onClick={handleCloseModal}
//           />
//           <div className="bg-white w-full max-w-6xl max-h-[90vh] overflow-y-auto z-10">
//             <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Détails de l'abonnement - {selectedSubscription.code}
//               </h2>
//               <button
//                 onClick={handleCloseModal}
//                 className="p-1 hover:bg-gray-100 transition-colors rounded"
//               >
//                 <BiX size={20} />
//               </button>
//             </div>

//             <div className="p-3 sm:p-4">
//               {modalLoading ? (
//                 <div className="flex justify-center items-center py-8">
//                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
//                 </div>
//               ) : (
//                 <>
//                   {/* Informations de l'abonnement */}
//                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50">
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Plan d'abonnement
//                       </p>
//                       <p className="font-poppins font-semibold text-[18px] text-title">
//                         {detailedSubscription?.plan?.name ||
//                           selectedSubscription.plan?.name ||
//                           "N/A"}
//                       </p>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Code du plan d'abonnement
//                       </p>
//                       <div
//                         className="flex items-center gap-2 cursor-pointer font-nunito text-[14px] font-semibold text-action"
//                         onClick={() =>
//                           copyToClipboard(
//                             detailedSubscription?.plan?.code ||
//                               selectedSubscription.plan?.code,
//                           )
//                         }
//                       >
//                         <span className="font-poppins font-semibold text-[18px] text-title">
//                           {detailedSubscription?.plan?.code ||
//                             selectedSubscription.plan?.code ||
//                             "N/A"}
//                         </span>
//                         <button
//                           onClick={() =>
//                             copyToClipboard(
//                               detailedSubscription?.plan?.code ||
//                                 selectedSubscription.plan?.code,
//                             )
//                           }
//                           className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
//                         >
//                           <BiCopy size={16} />
//                         </button>
//                       </div>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Quantité
//                       </p>
//                       <p className="font-poppins font-semibold text-[18px] text-title">
//                         {detailedSubscription?.quantity ||
//                           selectedSubscription.quantity}
//                       </p>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Montant total
//                       </p>
//                       <p className="font-poppins font-semibold text-[18px] text-title">
//                         {parseFloat(
//                           detailedSubscription?.totalAmount ||
//                             selectedSubscription.totalAmount,
//                         ).toLocaleString()}{" "}
//                         FCFA
//                       </p>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Statut
//                       </p>
//                       <p className="font-poppins font-semibold text-[18px] text-title">
//                         <span
//                           className={`px-2 py-1 font-nunito text-[14px] font-semibold text-action ${getStatusColor(
//                             detailedSubscription?.status ||
//                               selectedSubscription.status,
//                           )}`}
//                         >
//                           {getStatusLabel(
//                             detailedSubscription?.status ||
//                               selectedSubscription.status,
//                           )}
//                         </span>
//                       </p>
//                     </div>
//                     <div>
//                       <p className="font-poppins font-medium text-[15px] text-subtitle">
//                         Date de souscription
//                       </p>
//                       <p className="font-poppins font-semibold text-[18px] text-title">
//                         {new Date(
//                           detailedSubscription?.created_at ||
//                             selectedSubscription.created_at,
//                         ).toLocaleDateString("fr-FR", {
//                           day: "2-digit",
//                           month: "2-digit",
//                           year: "numeric",
//                           hour: "2-digit",
//                           minute: "2-digit",
//                         })}
//                       </p>
//                     </div>
//                   </div>
//                   {/* Services inclus dans le plan */}
//                   <div className="mb-6">
//                     <h3 className="font-poppins font-semibold text-[18px] text-title mb-3 sm:mb-4 flex items-center gap-2">
//                       <BiListUl className="text-secondary-600" size={16} />
//                       Services inclus dans le plan
//                     </h3>

//                     {!detailedSubscription?.plan?.includedServices?.length &&
//                     !selectedSubscription.plan?.includedServices?.length ? (
//                       <p className="font-nunito text-[14px] text-text text-center py-4">
//                         Aucun service inclus dans ce plan
//                       </p>
//                     ) : (
//                       <div className="space-y-2 sm:space-y-3">
//                         {(
//                           detailedSubscription?.plan?.includedServices ||
//                           selectedSubscription.plan?.includedServices ||
//                           []
//                         ).map((includedService, index) => (
//                           <div
//                             key={index}
//                             className="flex items-center justify-between p-2 sm:p-3 border border-gray-200 rounded"
//                           >
//                             <div className="flex-1">
//                               <p className="font-poppins font-semibold text-[18px] text-title">
//                                 {includedService.service?.nom ||
//                                   `Service ${index + 1}`}
//                               </p>
//                               <p className="font-nunito text-[14px] text-text">
//                                 Quantité incluse: {includedService.quantity}
//                               </p>
//                               {includedService.service?.description && (
//                                 <p className="font-nunito text-[14px] text-text mt-1">
//                                   {includedService.service.description}
//                                 </p>
//                               )}
//                             </div>
//                             <div className="text-right">
//                               <p className="font-nunito text-[14px] text-text">
//                                 Inclus dans l'abonnement
//                               </p>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                   {/* Cartes générées */}
//                   {detailedSubscription?.cards &&
//                     detailedSubscription.cards.length > 0 && (
//                       <div>
//                         <h3 className="font-poppins font-semibold text-[18px] text-title mb-3 sm:mb-4 flex items-center gap-2">
//                           <BiCreditCard
//                             className="text-secondary-600"
//                             size={16}
//                           />
//                           Cartes générées ({detailedSubscription.cards.length})
//                         </h3>

//                         <div className="space-y-3">
//                           {detailedSubscription.cards.map((card, index) => (
//                             <div
//                               key={card.id}
//                               className="border border-gray-200 p-3 sm:p-4 flex flex-col items-end"
//                             >
//                               <div className="w-full flex flex-row items-center justify-between gap-2 mb-3">
//                                 <div className="flex items-center gap-2">
//                                   {getCardStatusIcon(card.status)}
//                                   <span className="font-poppins font-semibold text-[18px] text-title">
//                                     Carte {index + 1} - {card.code}
//                                   </span>
//                                 </div>
//                                 <span
//                                   className={`px-2 py-1 font-nunito text-[14px] font-semibold text-action ${getCardStatusColor(
//                                     card.status,
//                                   )}`}
//                                 >
//                                   {getCardStatusLabel(card.status)}
//                                 </span>
//                               </div>

//                               {card.status === "UNUSED" && (
//                                 <button
//                                   onClick={() => handleUseCard(card)}
//                                   className="flex items-center gap-1 font-nunito text-[14px] font-semibold text-action text-green-600 hover:text-green-700"
//                                 >
//                                   <BiCheckCircle size={14} />
//                                   Marquer comme utilisée
//                                 </button>
//                               )}
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     )}
//                 </>
//               )}
//             </div>

//             <div className="p-3 sm:p-4 border-t border-gray-200 flex justify-end">
//               <button
//                 onClick={handleCloseModal}
//                 className="px-3 sm:px-4 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors"
//               >
//                 Fermer
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
